import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { SafetyShieldFilledIcon, StarFilledIcon } from '@components/Pages/IssuesPage/Board/icons';
import { formatDateRange } from '../model/formatDateRange';
import { DayShading } from './DayShading';
import { capChips, isCompact, laneCap, type IIssueChip, type IIssueLane, type IPlanWindow } from './workPlanModel';
import styles from './issueLane.module.css';

export interface IIssueLaneProps {
  lane: IIssueLane;
  window: IPlanWindow;
  today: string;
  onOpenIssue: (key: string) => void;
}

/** Issues without a due date the rail cell lists before its count. */
const NO_DUE_SHOWN = 2;

interface IIssueChipProps {
  chip: IIssueChip;
  /** A compact (4-week) window: the chip collapses to its marker. */
  compact: boolean;
  dueLabel: string | null;
  onOpen: (key: string) => void;
}

/** One issue in the Daily List's vocabulary: its dot (red for safety), the
 *  critical star and the safety shield; overdue tints it, completed fades it.
 *  The full title rides in the marker tooltip. */
function IssueChip({ chip, compact, dueLabel, onOpen }: IIssueChipProps) {
  return (
    <Tooltip variant="marker" content={<TooltipContent title={chip.title} lines={dueLabel ? [dueLabel] : []} />}>
      <button
        type="button"
        className={styles.chip}
        data-critical={chip.critical || undefined}
        data-late={chip.late || undefined}
        data-completed={chip.status === 'completed' || undefined}
        aria-label={chip.title}
        onClick={() => onOpen(chip.key)}
      >
        <StatusDot color={chip.safety ? 'var(--red)' : 'var(--g400)'} size={6} />
        {!compact && <span className={styles.title}>{chip.title}</span>}
        {chip.critical && <StarFilledIcon size={11} className={styles.star} />}
        {chip.safety && <SafetyShieldFilledIcon size={11} gradient />}
      </button>
    </Tooltip>
  );
}

/**
 * The pinned first row: every issue the Status checkboxes show — the open
 * ones by default — on its due day (decision 6), capped
 * per cell with "+N more" — which opens the lane up to show them all. The rail
 * cell holds the issues with no due date and the count that fell due before
 * the window. Issues are never placed on a promise's row: no data links them.
 */
export function IssueLane({ lane, window, today, onOpenIssue }: IIssueLaneProps) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const compact = isCompact(window);
  const cap = expanded ? Infinity : laneCap(window);
  const currentYear = Number(today.slice(0, 4));
  const noDue = capChips(lane.noDue, NO_DUE_SHOWN);
  const notes = [
    t('activities.workPlan.issues.noDue', { count: lane.noDue.length }),
    ...(lane.lateBefore > 0 ? [t('activities.workPlan.issues.lateBefore', { count: lane.lateBefore })] : []),
  ];
  return (
    <>
      <div className={styles.rail}>
        <span className={styles.head}>
          <span className={styles.heading}>{t('activities.workPlan.issues.title')}</span>
          {t('activities.workPlan.issues.byDue')}
        </span>
        <span className={styles.notes}>{notes.join(' · ')}</span>
        {noDue.shown.map((chip) => (
          <IssueChip key={chip.key} chip={chip} compact={false} dueLabel={null} onOpen={onOpenIssue} />
        ))}
      </div>
      <div className={styles.time}>
        <DayShading window={window} />
        {window.days.map((day, i) => {
          const chips = lane.byDay.get(day.date);
          if (!chips) return null;
          const { shown, more } = capChips(chips, cap);
          const dueLabel = formatDateRange(day.date, day.date, i18n.language, currentYear);
          return (
            <div key={day.date} className={styles.cell} style={{ gridColumn: i + 1 }}>
              {shown.map((chip) => (
                <IssueChip key={chip.key} chip={chip} compact={compact} dueLabel={dueLabel} onOpen={onOpenIssue} />
              ))}
              {more > 0 && (
                <button type="button" className={styles.more} onClick={() => setExpanded(true)}>
                  {compact ? `+${more}` : t('activities.workPlan.issues.more', { count: more })}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
